import React, { useCallback, useEffect, useRef } from 'react';
import { style } from 'typestyle';
import AuthorizedPage from '@src/components/AuthorizedPage';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { MessagesScroller } from './MessagesScroller';
import { Services } from '@src/types/Services';
import { Message } from './Message';
import { MessageWriter } from './MessageWriter';
import { ADALONG_COLORS } from '@src/styles';
import { MESSAGE_REFRESH_INTERVAL_MS } from '@src/config';
import { DmConversation } from '@adalong/schemas';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import { Loader } from '@src/components/Loader';
import useLocalStorage from '@src/hooks/CRM/useLocalStorage';
import { useConversationDataManager } from '@src/hooks/CRM/useConversationDataManager';
import { useConversationActions } from '@src/hooks/CRM/useConversationActions';
import { useMessageManager } from '@src/hooks/CRM/useMessageManager';

export function MessagesPage(props: {
  conversation?: {
    conversationId: string;
    target: string;
    interlocutor: string;
  };
  services: Services;
  onConversationLoad?: (conversation: any) => void;
}) {
  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Save the draft in local storage
  const [newMessage, setNewMessage] = useLocalStorage(`CRM_newMessage`, '');

  const { findConversation, isOnline } = useConversationDataManager();
  const { markConversationAsRead } = useConversationActions();
  
  // Find the current conversation
  const currentConversation = props.conversation
    ? findConversation(props.conversation.target, props.conversation.interlocutor)
    : undefined;

  // Get messages for this conversation
  const {
    messages,
    isLoading: isLoadingMessages,
    isSending: isSendingMessage,
    error: messageError,
    sendMessage,
    nextRefreshIn,
    refreshStatus,
  } = useMessageManager(currentConversation, {
    enabled: !!currentConversation,
    autoRefreshInterval: MESSAGE_REFRESH_INTERVAL_MS,
  });

  // Notify parent when conversation is loaded
  useEffect(() => {
    if (currentConversation && props.onConversationLoad) {
      props.onConversationLoad(currentConversation.raw);
    }
  }, [currentConversation, props.onConversationLoad]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (currentConversation && messages && messages.length > 0) {
      const timer = setTimeout(() => {
        markConversationAsRead(
          currentConversation.targetId,
          currentConversation.interlocutorId
        );
      }, 2000); 
      
      return () => clearTimeout(timer);
    }
  }, [currentConversation?.id, messages, markConversationAsRead]);

  // Simple, reliable scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // Use a reasonable padding to ensure we reach the bottom
      container.scrollTop = container.scrollHeight + 100;
    }
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    scrollToBottom();
    setTimeout(scrollToBottom, 300); 
  }, [messages?.length, scrollToBottom]);

  const onSendMessage = useCallback(
    async (text: string) => {
      try {
        await sendMessage(text);
        setNewMessage(''); 
        setTimeout(scrollToBottom, 100);
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    },
    [sendMessage, setNewMessage, scrollToBottom]
  );

  const onChangeMessage = useCallback((text: string) => {
    setNewMessage(text);
  }, [setNewMessage]);

  // Convert messages to UI components
  const populateConversationMessages = (messageList: typeof messages) => {
    if (!messageList || !currentConversation) return [];

    const sortedMessages = [...messageList].sort((a, b) => {
      const aTime = new Date(a.receivedDate || a.created_at || '').getTime();
      const bTime = new Date(b.receivedDate || b.created_at || '').getTime();
      return aTime - bTime; // Oldest first
    });

    return sortedMessages.map((message, idx) => {
      const messageDate = new Date(message.receivedDate || message.created_at || '');
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const isToday = messageDate.toDateString() === today.toDateString();
      const isYesterday = messageDate.toDateString() === yesterday.toDateString();

      let msgDateStr;
      if (isToday) {
        msgDateStr = 'Today';
      } else if (isYesterday) {
        msgDateStr = 'Yesterday';
      } else {
        msgDateStr = messageDate.toLocaleDateString();
      }

      const prevMessage = idx > 0 ? sortedMessages[idx - 1] : null;
      const prevMessageDate = prevMessage ? new Date(prevMessage.receivedDate || prevMessage.created_at || '') : null;
      const showDate = !prevMessageDate || prevMessageDate.toDateString() !== messageDate.toDateString();

      const selfMessage = message.networkSenderId === currentConversation.targetId;
      const senderInfo = selfMessage 
        ? { name: user?.displayName || 'You', id: currentConversation.targetId }
        : { name: currentConversation.interlocutorUsername, id: currentConversation.interlocutorId };

      const messageKey = message.networkMessageId || `${message.receivedDate || message.created_at}-${idx}`;

      return (
        <React.Fragment key={messageKey}>
          {showDate ? (
            <div className={dateSeparatorContainerClass}>
              <span className={dateSeparatorClass}>
                {msgDateStr}
              </span>
            </div>
          ) : null }
          <Message message={message} senderInfo={senderInfo} self={selfMessage} />
        </React.Fragment>
      );
    });
  };

  const pageContainerClass = style({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: ADALONG_COLORS.DARK_BLACK,
  });

  const messagesContainerClass = style({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%', 
  });

  const scrollContainerClass = style({
    flex: 1,
    minHeight: 0, // Important: allows flex child to shrink below content size
    backgroundColor: 'transparent',
    position: 'relative' as const,
  });

  const statusContainerClass = style({
    padding: '8px 16px',
    backgroundColor: ADALONG_COLORS.SMOOTH_BLACK, 
    fontSize: '12px',
    textAlign: 'center',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexShrink: 0,
  });

  const messageWriterContainerClass = style({
    backgroundColor: ADALONG_COLORS.SMOOTH_BLACK, 
    flexShrink: 0, 
  });

  const dateSeparatorContainerClass = style({
    margin: '16px 0 8px 0',
    display: 'flex',
    justifyContent: 'center',
    width: '100%'
  });

  const dateSeparatorClass = style({
    fontSize: '13px',
    color: '#8a8a8a',
    backgroundColor: '#2a2a2a',
    padding: '4px 12px',
    borderRadius: '12px',
    textAlign: 'center',
    fontWeight: 500
  });

  // Render conversation not found
  if (props.conversation && !currentConversation) {
    return (
      <AuthorizedPage tab="crm" user={user}>
        <div className={containerClass}>
          <div className={warningAlertClass}>
            <h3 className={titleClass}>Conversation not found</h3>
            <p className={messageTextClass}>
              Conversation not found. {!isOnline && 'You appear to be offline. '}
              Please try refreshing the page or selecting a different conversation.
            </p>
          </div>
        </div>
      </AuthorizedPage>
    );
  }

  // Render no conversation selected
  if (!props.conversation) {
    return (
      <AuthorizedPage tab="crm" user={user}>
        <div className={containerClass}>
          <div className={loadingContainerClass}>
            <Loader size={40} />
            <h3 className={titleClass} style={{ marginTop: '16px' }}>Select a conversation to start messaging</h3>
          </div>
        </div>
      </AuthorizedPage>
    );
  }

  // Render loading state
  if (isLoadingMessages && !messages) {
    return (
      <AuthorizedPage tab="crm" user={user}>
        <div className={containerClass}>
          <div className={loadingContainerClass}>
            <Loader size={40} />
            <p className={messageTextClass} style={{ marginTop: '16px' }}>Loading conversation...</p>
          </div>
        </div>
      </AuthorizedPage>
    );
  }

  // Calculate conversation message items
  const conversationMessageItems = populateConversationMessages(messages);

  // Main render
  return (
    <AuthorizedPage tab="crm" user={user}>
      <div className={pageContainerClass}>
        {messageError ? (
          <div className={errorAlertClass}>
            <p className={messageTextClass}>{messageError}</p>
          </div>
        ) : null }
        
        <div className={messagesContainerClass}>
          <div className={scrollContainerClass}>
            <PerfectScrollbar
              containerRef={scrollContainerRef}
              options={{
                suppressScrollX: true, // Disable horizontal scrollbar
                wheelPropagation: false,
                minScrollbarLength: 20,
              }}
              style={{ 
                height: '100%',
                padding: '16px',
              }}
            >
              <MessagesScroller items={conversationMessageItems} isLoading={isLoadingMessages} />
            </PerfectScrollbar>
          </div>
          
          <div className={statusContainerClass}>
            {!isOnline ? (
              <span style={{ color: '#ff6b6b' }}>⚠️ No Internet Connection - updates paused</span>
            ) : messageError ? (
              <span style={{ color: '#ff6b6b' }}>❌ Error loading messages. Please wait patiently for auto-retrying...</span>
            ) : refreshStatus === 'refreshing' || isLoadingMessages ? (
              <>
                <Loader size={16} />
                <span style={{ color: '#4CAF50' }}>Refreshing messages...</span>
              </>
            ) : refreshStatus === 'success' ? (
              <span style={{ color: '#4CAF50' }}>✅ Messages up to date</span>
            ) : refreshStatus === 'error' ? (
              <span style={{ color: '#ff6b6b' }}>❌ Refresh failed. Please wait...</span>
            ) : nextRefreshIn && nextRefreshIn > 0 ? (
              <span style={{ color: ADALONG_COLORS.LIGHT_GRAY  }}>
                Next refresh in {nextRefreshIn}s
              </span>
            ) : (
              <span style={{ color: ADALONG_COLORS.LIGHT_GRAY }}>Ready</span>
            )}
          </div>
          
          <div className={messageWriterContainerClass}>
            <MessageWriter 
              text={newMessage} 
              isLoading={isSendingMessage} 
              onSend={onSendMessage} 
              onChange={onChangeMessage}
              conversation={currentConversation?.type === 'remote' || currentConversation?.type === 'merged' 
                ? currentConversation.raw as DmConversation 
                : undefined}
              creatorName={currentConversation?.interlocutorUsername}
            />
          </div>
        </div>
      </div>
    </AuthorizedPage>
  );
}

const containerClass = style({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: ADALONG_COLORS.DARK_BLACK,
  color: 'white',
});

const errorAlertClass = style({
  backgroundColor: 'rgba(244, 67, 54, 0.1)',
  border: '1px solid rgba(244, 67, 54, 0.3)',
  borderRadius: '8px',
  padding: '16px',
  margin: '16px',
  color: '#ff6b6b',
});

const warningAlertClass = style({
  backgroundColor: 'rgba(255, 152, 0, 0.1)',
  border: '1px solid rgba(255, 152, 0, 0.3)',
  borderRadius: '8px',
  padding: '16px',
  margin: '16px',
  color: '#ff9800',
});

const messageTextClass = style({
  fontSize: '15px',
  lineHeight: '1.4',
  color: 'white',
  margin: 0,
});

const titleClass = style({
  fontSize: '18px',
  fontWeight: 600,
  color: 'white',
  margin: 0,
  marginBottom: '8px',
});

const subtitleClass = style({
  fontSize: '14px',
  color: ADALONG_COLORS.LIGHT_GRAY,
  margin: 0,
});

const loadingContainerClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  height: '200px',
  gap: '16px',
});